import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

interface OfficeLocation {
  city: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
}

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <section class="contact-page">
      <div class="container">
        <header class="page-header">
          <h1>Contact Us</h1>
          <p class="subtitle">Get in touch with our team for personalized support and solutions</p>
        </header>

        <div class="contact-grid">
          <div class="contact-form-container">
            <h2>Send us a Message</h2>
            <form [formGroup]="contactForm" (ngSubmit)="onSubmit()" class="contact-form">
              <div class="form-group">
                <label for="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  formControlName="name" 
                  placeholder="Enter your full name"
                  [class.error]="isFieldInvalid('name')"
                >
                <div class="error-message" *ngIf="isFieldInvalid('name')">
                  Please enter your name
                </div>
              </div>

              <div class="form-group">
                <label for="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  formControlName="email" 
                  placeholder="Enter your email address"
                  [class.error]="isFieldInvalid('email')"
                >
                <div class="error-message" *ngIf="isFieldInvalid('email')">
                  Please enter a valid email address
                </div>
              </div>

              <div class="form-group">
                <label for="company">Company Name *</label>
                <input 
                  type="text" 
                  id="company" 
                  formControlName="company" 
                  placeholder="Enter your company name"
                  [class.error]="isFieldInvalid('company')"
                >
                <div class="error-message" *ngIf="isFieldInvalid('company')">
                  Please enter your company name
                </div>
              </div>

              <div class="form-group">
                <label for="subject">Subject *</label>
                <select 
                  id="subject" 
                  formControlName="subject"
                  [class.error]="isFieldInvalid('subject')"
                >
                  <option value="">Select a subject</option>
                  <option value="sales">Sales Inquiry</option>
                  <option value="support">Technical Support</option>
                  <option value="partnership">Partnership Opportunity</option>
                  <option value="other">Other</option>
                </select>
                <div class="error-message" *ngIf="isFieldInvalid('subject')">
                  Please select a subject
                </div>
              </div>

              <div class="form-group">
                <label for="message">Message *</label>
                <textarea 
                  id="message" 
                  formControlName="message" 
                  rows="5" 
                  placeholder="How can we help you?"
                  [class.error]="isFieldInvalid('message')"
                ></textarea>
                <div class="error-message" *ngIf="isFieldInvalid('message')">
                  Please enter your message
                </div>
              </div>

              <button type="submit" class="submit-button" [disabled]="!contactForm.valid || isSubmitting">
                {{ isSubmitting ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>

          <div class="office-locations">
            <h2>Our Offices</h2>
            <div class="locations-grid">
              <div class="location-card" *ngFor="let office of offices">
                <h3>{{ office.city }}</h3>
                <div class="location-details">
                  <p class="address">{{ office.address }}</p>
                  <p class="contact">
                    <span class="label">Phone:</span> {{ office.phone }}
                  </p>
                  <p class="contact">
                    <span class="label">Email:</span> {{ office.email }}
                  </p>
                  <p class="hours">
                    <span class="label">Hours:</span> {{ office.hours }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .contact-page {
      padding: 4rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .page-header {
      text-align: center;
      margin-bottom: 3rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .subtitle {
      color: var(--text-secondary);
      font-size: 1.1rem;
    }

    .contact-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
    }

    .contact-form-container {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    h2 {
      font-size: 1.5rem;
      color: var(--text-primary);
      margin-bottom: 1.5rem;
    }

    .contact-form {
      display: grid;
      gap: 1.5rem;
    }

    .form-group {
      display: grid;
      gap: 0.5rem;
    }

    label {
      color: var(--text-primary);
      font-weight: 500;
    }

    input, select, textarea {
      padding: 0.75rem;
      border: 1px solid var(--border-color);
      border-radius: 4px;
      font-size: 1rem;
      width: 100%;
      transition: border-color 0.3s;
    }

    input:focus, select:focus, textarea:focus {
      outline: none;
      border-color: var(--primary-color);
    }

    input.error, select.error, textarea.error {
      border-color: var(--error-color);
    }

    .error-message {
      color: var(--error-color);
      font-size: 0.875rem;
    }

    .submit-button {
      background: var(--primary-color);
      color: white;
      padding: 1rem;
      border: none;
      border-radius: 4px;
      font-size: 1rem;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
    }

    .submit-button:hover:not(:disabled) {
      background: var(--primary-dark);
    }

    .submit-button:disabled {
      background: var(--disabled-color);
      cursor: not-allowed;
    }

    .office-locations {
      display: grid;
      gap: 2rem;
    }

    .locations-grid {
      display: grid;
      gap: 1.5rem;
    }

    .location-card {
      background: white;
      padding: 1.5rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .location-card h3 {
      color: var(--primary-color);
      font-size: 1.25rem;
      margin-bottom: 1rem;
    }

    .location-details {
      display: grid;
      gap: 0.75rem;
    }

    .address {
      color: var(--text-primary);
      line-height: 1.5;
    }

    .contact, .hours {
      color: var(--text-secondary);
    }

    .label {
      font-weight: 500;
      color: var(--text-primary);
    }

    @media (max-width: 992px) {
      .contact-grid {
        grid-template-columns: 1fr;
      }

      .page-header {
        margin-bottom: 2rem;
      }

      h1 {
        font-size: 2rem;
      }
    }

    @media (max-width: 576px) {
      .contact-page {
        padding: 2rem 0;
      }

      .contact-form-container,
      .location-card {
        padding: 1.5rem;
      }

      h2 {
        font-size: 1.25rem;
      }
    }
  `]
})
export class ContactComponent {
  contactForm: FormGroup;
  isSubmitting = false;

  offices: OfficeLocation[] = [
    {
      city: 'Los Angeles (Headquarters)',
      address: '1234 Innovation Drive, Suite 500, Los Angeles, CA 90001',
      phone: '+1 (800) 555-0123',
      email: 'la&#64;industryintelligence.com',
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM PST'
    },
    {
      city: 'New York',
      address: '567 Madison Avenue, 15th Floor, New York, NY 10022',
      phone: '+1 (800) 555-0124',
      email: 'nyc&#64;industryintelligence.com',
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM EST'
    },
    {
      city: 'London',
      address: '89 Queen Street, Canary Wharf, London E14 9SR, UK',
      phone: '+44 20 7123 4567',
      email: 'london&#64;industryintelligence.com',
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM GMT'
    }
  ];

  constructor(private fb: FormBuilder) {
    this.contactForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      company: ['', Validators.required],
      subject: ['', Validators.required],
      message: ['', Validators.required]
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contactForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  async onSubmit() {
    if (this.contactForm.valid) {
      this.isSubmitting = true;
      try {
        // Here you would typically make an API call to submit the form
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulated API call
        console.log('Form submitted:', this.contactForm.value);
        this.contactForm.reset();
        // You would typically show a success message here
      } catch (error) {
        console.error('Error submitting form:', error);
        // You would typically show an error message here
      } finally {
        this.isSubmitting = false;
      }
    } else {
      Object.keys(this.contactForm.controls).forEach(key => {
        const control = this.contactForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }
} 